/**
 * WIP report — Work-In-Progress по объектам в работе.
 *
 * Для каждого незавершённого объекта: контракт, факт-COGS, AR,
 * прогресс по себестоимости (cogs / contract*0.75), задержка против plan_end.
 */
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { Spinner } from "@/components/Spinner";
import { FinanceLayout } from "@/components/finance/FinanceLayout";
import {
  fetchWipReport, updateObjectPlannedCost, formatRub,
  type WipObject,
} from "@/api/finance";
import { ApiError } from "@/api/client";


function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const [y, m, d] = iso.slice(0, 10).split("-");
  return d && m && y ? `${d}.${m}.${y}` : iso;
}


function ProgressBar({ pct, tone }: { pct: number; tone: string }) {
  const w = Math.max(0, Math.min(100, pct));
  return (
    <div style={{ position: "relative", height: 6, width: "100%", background: "var(--line-soft)", borderRadius: 999, overflow: "hidden" }}>
      <div style={{ width: `${w}%`, height: "100%", background: tone, transition: "width 200ms ease" }} />
    </div>
  );
}


function progressTone(pct: number): string {
  if (pct < 30) return "var(--mute-2)";
  if (pct < 90) return "var(--amber)";
  return "var(--green)";
}


function PlannedCostCell({ o }: { o: WipObject }) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: (v: number | null) => updateObjectPlannedCost(o.object_id, v),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["wip"] }),
  });
  const onClick = () => {
    const cur = o.planned_cost_is_default ? "" : String(o.planned_cost);
    const v = window.prompt(
      `Плановая себестоимость объекта "${o.client_name}" (контракт ${formatRub(o.contract_sum, { unit: true })}).\n` +
      `Сейчас: ${o.planned_cost_is_default ? "по умолчанию 75% контракта" : "задано вручную"}.\n\n` +
      `Введи новое число в рублях (пусто = сброс):`,
      cur,
    );
    if (v === null) return;
    const trimmed = v.trim().replace(",", ".");
    if (trimmed === "") {
      mut.mutate(null);
      return;
    }
    const num = parseFloat(trimmed);
    if (isNaN(num) || num < 0) {
      window.alert("Введи положительное число");
      return;
    }
    mut.mutate(num);
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={mut.isPending}
      style={{
        background: "none", border: "none", padding: 0, margin: 0,
        font: "inherit", color: "inherit", cursor: "pointer",
        textAlign: "right", width: "100%", whiteSpace: "nowrap",
      }}
      title="Кликни чтобы изменить плановую себестоимость"
    >
      <div style={{ fontWeight: o.planned_cost_is_default ? 400 : 600 }}>
        {formatRub(o.planned_cost, { unit: true })}
        {o.planned_cost_is_default && <span style={{ color: "var(--mute-2)", fontSize: 10, marginLeft: 4 }}>(75% def)</span>}
      </div>
      {!o.planned_cost_is_default && o.cost_overrun !== 0 && (
        <div style={{
          fontSize: 11,
          color: o.cost_overrun > 0 ? "var(--brick)" : "var(--green)",
        }}>
          {o.cost_overrun > 0 ? "перерасход " : "экономия "}
          {formatRub(Math.abs(o.cost_overrun), { unit: true })}
        </div>
      )}
    </button>
  );
}


function WipRow({ o }: { o: WipObject }) {
  const overdue = o.delay_days !== null && o.delay_days > 0;
  return (
    <tr className="ar-row" style={{ borderBottom: "1px solid var(--line-soft)" }}>
      <td style={{
        paddingLeft: 14,
        borderLeft: `4px solid ${progressTone(o.progress_pct)}`,
        verticalAlign: "top",
      }}>
        <div style={{ fontWeight: 600 }}>
          {o.client_name}
          {o.is_promotion && (
            <span style={{
              marginLeft: 8, padding: "1px 7px", borderRadius: 999,
              background: "rgba(212,167,58,0.15)", color: "#8a6e10",
              border: "1px solid rgba(212,167,58,0.35)",
              fontSize: 10, fontWeight: 600, textTransform: "uppercase",
            }}>акция</span>
          )}
        </div>
        {o.address && <div style={{ fontSize: 11, color: "var(--mute)", marginTop: 2 }}>{o.address}</div>}
        {o.region && <div style={{ fontSize: 11, color: "var(--mute)" }}>{o.region}{o.sqm ? ` · ${o.sqm} м²` : ""}</div>}
      </td>
      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
        {formatRub(o.contract_sum, { unit: true })}
      </td>
      <td style={{ textAlign: "right" }}>
        <PlannedCostCell o={o} />
      </td>
      <td style={{ textAlign: "right", whiteSpace: "nowrap", color: "var(--ink-3)" }}>
        {formatRub(o.cogs_spent, { unit: true })}
        <div style={{ fontSize: 10, color: "var(--mute)" }}>
          мат {formatRub(o.mat_spent, { unit: true })} · ФОТ {formatRub(o.fot_spent, { unit: true })}
        </div>
      </td>
      <td style={{ minWidth: 140 }}>
        <ProgressBar pct={o.progress_pct} tone={progressTone(o.progress_pct)} />
        <div style={{ fontSize: 11, color: "var(--mute)", marginTop: 2, textAlign: "right" }}>
          {o.progress_pct.toFixed(0)}% от плана COGS
        </div>
      </td>
      <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
        <span style={{
          fontFamily: "var(--serif)", fontWeight: 700, fontSize: 14,
          color: (o.expected_gp_pct ?? 0) >= 20 ? "var(--green)" : "var(--brick)",
        }}>
          {o.expected_gp_pct !== null ? `${o.expected_gp_pct}%` : "—"}
        </span>
        <div style={{ fontSize: 11, color: "var(--mute)" }}>
          {formatRub(o.expected_margin, { unit: true })}
        </div>
      </td>
      <td style={{ textAlign: "right", whiteSpace: "nowrap", color: o.ar > 0 ? "var(--brick)" : "var(--ink-3)" }}>
        {formatRub(o.ar, { unit: true })}
        <div style={{ fontSize: 11, color: "var(--mute)" }}>
          получено {o.collected_pct.toFixed(0)}%
        </div>
      </td>
      <td style={{ whiteSpace: "nowrap", fontSize: 12 }}>
        {o.planned_end ? (
          <>
            <div style={{ color: overdue ? "var(--brick)" : "var(--ink-3)" }}>
              {fmtDate(o.planned_end)}
            </div>
            {overdue && (
              <div style={{ color: "var(--brick)", fontSize: 11 }}>
                просрочка {o.delay_days} дн
              </div>
            )}
          </>
        ) : <span style={{ color: "var(--mute-2)" }}>не указано</span>}
      </td>
    </tr>
  );
}


export function FinanceWipPage() {
  const [region, setRegion] = useState<"" | "Москва" | "Юг">("");
  const [bucket, setBucket] = useState<"" | "not_started" | "in_progress" | "almost_done">("");

  const q = useQuery({
    queryKey: ["wip", region],
    queryFn: () => fetchWipReport({ region: region || undefined }),
  });

  const items = useMemo(() => {
    const all = q.data?.objects || [];
    if (!bucket) return all;
    return all.filter(o => {
      if (bucket === "not_started")  return o.progress_pct <= 1;
      if (bucket === "in_progress")  return o.progress_pct > 1 && o.progress_pct < 90;
      if (bucket === "almost_done")  return o.progress_pct >= 90;
      return true;
    });
  }, [q.data, bucket]);

  if (q.isLoading) return <FinanceLayout><Spinner size="lg" label="Загрузка WIP…" /></FinanceLayout>;
  if (q.error) {
    const err = q.error;
    if (err instanceof ApiError && err.status === 403) {
      return <FinanceLayout><div className="alert alert-amber"><div className="alert-body">Только для админа</div></div></FinanceLayout>;
    }
    return <FinanceLayout><div className="alert alert-red"><div className="alert-body">{(err as Error).message}</div></div></FinanceLayout>;
  }

  const data = q.data!;
  const expectedMargin = data.totals.expected_gross_margin;

  return (
    <FinanceLayout>
      <div className="hero-overview" style={{ marginBottom: 20 }}>
        <div>
          <div className="hero-headline">
            {formatRub(data.totals.contract_sum, { unit: true })}
          </div>
          <div style={{ color: "var(--mute-2)", fontSize: 13, marginTop: 8, lineHeight: 1.5 }}>
            Незавершённых объектов: {data.totals.objects_count}.<br />
            Расходы (факт): {formatRub(data.totals.cogs_spent, { unit: true })}.
          </div>
        </div>
        <div className="hero-stat">
          <div className="hero-stat-label">Ожидаемая маржа</div>
          <div className="hero-stat-value" style={{ color: "#82E682" }}>
            {formatRub(expectedMargin, { unit: true })}
          </div>
          <div style={{ color: "var(--mute-2)", fontSize: 12, marginTop: 6 }}>
            При типовой марже 25% от контракта
          </div>
        </div>
        <div className="hero-stat">
          <div className="hero-stat-label">Дебиторка по WIP</div>
          <div className="hero-stat-value" style={{ color: data.totals.ar > 0 ? "#FF6B6B" : "#fff" }}>
            {formatRub(data.totals.ar, { unit: true })}
          </div>
          <div style={{ color: "var(--mute-2)", fontSize: 12, marginTop: 6 }}>
            Получено: {formatRub(data.totals.cash_got, { unit: true })}
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12, marginBottom: 16 }}>
        {(["not_started", "in_progress", "almost_done"] as const).map(b => {
          const info = data.by_progress[b];
          const labels = {
            not_started:  { label: "Не начатые",     tone: "var(--mute-2)" },
            in_progress:  { label: "В работе",       tone: "var(--amber)" },
            almost_done:  { label: "Завершаются",    tone: "var(--green)" },
          };
          const isActive = bucket === b;
          return (
            <button
              key={b}
              type="button"
              className="kpi"
              onClick={() => setBucket(bucket === b ? "" : b)}
              style={{
                textAlign: "left",
                cursor: info.objects_count > 0 ? "pointer" : "default",
                opacity: info.objects_count > 0 ? 1 : 0.5,
                outline: isActive ? `2px solid ${labels[b].tone}` : "none",
                outlineOffset: 2,
                fontFamily: "inherit",
              }}
            >
              <div className="kpi-label">{labels[b].label}</div>
              <div className="kpi-value" style={{ fontSize: 28, whiteSpace: "nowrap", color: labels[b].tone }}>
                {info.objects_count}
              </div>
              <div className="kpi-foot">
                <span style={{ fontSize: 12, color: "var(--mute)" }}>
                  контракты {formatRub(info.contract_sum, { unit: true })}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="card card-tight" style={{ marginBottom: 16, padding: "12px 16px" }}>
        <div className="chip-row" style={{ marginBottom: 0 }}>
          <span style={{ fontSize: 12, color: "var(--mute)", marginRight: 4, alignSelf: "center" }}>Регион:</span>
          {(["", "Москва", "Юг"] as const).map(r => (
            <button key={r || "all"} className={`chip ${region === r ? "active" : ""}`} onClick={() => setRegion(r)}>
              {r || "Все"}
            </button>
          ))}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="card" style={{ textAlign: "center", color: "var(--mute)", padding: 32 }}>
          Нет объектов в работе для выбранных фильтров.
        </div>
      ) : (
        <div className="card" style={{ padding: 0, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{
                background: "var(--paper-2)", color: "var(--mute)", fontSize: 11,
                textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 500,
              }}>
                <th style={{ textAlign: "left", padding: "10px 14px" }}>Клиент</th>
                <th style={{ textAlign: "right", padding: "10px 8px" }}>Контракт</th>
                <th style={{ textAlign: "right", padding: "10px 8px" }} title="Кликни на ячейку чтобы задать">План COGS</th>
                <th style={{ textAlign: "right", padding: "10px 8px" }}>Потрачено</th>
                <th style={{ textAlign: "left", padding: "10px 8px" }}>Прогресс</th>
                <th style={{ textAlign: "right", padding: "10px 8px" }}>Маржа план</th>
                <th style={{ textAlign: "right", padding: "10px 8px" }}>AR</th>
                <th style={{ textAlign: "left", padding: "10px 8px" }}>План завершения</th>
              </tr>
            </thead>
            <tbody>
              {items.map(o => <WipRow key={o.object_id} o={o} />)}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ marginTop: 16, padding: 12, background: "var(--paper-2)", borderRadius: 8, fontSize: 12, color: "var(--mute)", lineHeight: 1.5 }}>
        Объект считается «в работе», если <code>actual_end</code> не указан и статус не <i>completed/cancelled</i>.
        <b> «План COGS» можно задать вручную</b> кликом на ячейку (по умолчанию — 75% контракта).
        <b> «Прогресс по COGS» = факт / план</b>. Если факт превышает план — в ячейке плана появится <span style={{ color: "var(--brick)" }}>«перерасход»</span>;
        если меньше — <span style={{ color: "var(--green)" }}>«экономия»</span>. Для построчного план/факт по спецификации
        (Кровля/Стены/Фундамент/…) — отдельный модуль, в разработке.
      </div>

      <style>{`
        .ar-row > td { padding: 12px 8px; transition: background 100ms ease; }
        .ar-row:hover > td { background: var(--paper-2); }
      `}</style>
    </FinanceLayout>
  );
}
